import { useEffect, useRef, useState } from 'react';
import { createFileRoute, Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { Plug } from 'lucide-react';
import { createHubSpotIntegration } from '@/lib/apis';
import { hubspotIntegrationAtom } from '@/store';
import { useToast } from '@/hooks/useToast';
import type { HubSpotCallbackRequest } from '@/lib/types';

export const Route = createFileRoute('/_authenticated/_layout/settings/_layout/integrations')({
  component: Integrations,
  staticData: {
    title: 'Integrations',
    icon: Plug,
  },
});

function Integrations() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  // Captured once at mount: the HubSpot return lands on this bare path with
  // ?code=... (HUBSPOT_REDIRECT_URI), and the effect below cleans the URL
  // while the exchange runs — state, not a recomputation, keeps the page up.
  const [completingHubspot] = useState(
    () => new URLSearchParams(window.location.search).has('code')
  );
  // This route is the layout parent of the per-provider callback routes (`integrations/meta/callback`,
  // `integrations/hubspot/callback`). On those paths render the child via <Outlet/> and let it handle its own
  // provider — don't run the HubSpot callback handler below (it'd POST a Meta auth code to HubSpot → 400).
  const isCallbackSubRoute = pathname.includes('/callback');

  const queryClient = useQueryClient();
  const setHubspotIntegration = useSetAtom(hubspotIntegrationAtom);
  const { toast } = useToast();
  // Ref, not state: the OAuth `code` is single-use and StrictMode double-invokes effects in dev — a state
  // flag wouldn't be updated in time to block the second run, which would re-POST the consumed code (→ 400).
  const processedRef = useRef(false);

  // Once the exchange has settled, either way, the one Integrations surface
  // shows the result: this route never draws the page itself (#6670).
  const createIntegrationMutation = useMutation({
    mutationFn: (data: HubSpotCallbackRequest) => createHubSpotIntegration(data),
    onSuccess: (data) => {
      if (data.success && data.integration_status) {
        setHubspotIntegration(data.integration_status);
        queryClient.invalidateQueries({ queryKey: ['hubspot', 'integration', 'status'] });
      }
    },
    onError: (error) => {
      toast({
        title: 'Connection Failed',
        description: error.message || 'Failed to complete HubSpot integration',
        variant: 'destructive',
      });
    },
    onSettled: () => navigate({ to: '/settings/account', hash: 'integrations', replace: true }),
  });

  // Handle a HubSpot OAuth callback that lands on the bare /settings/integrations path (HUBSPOT_REDIRECT_URI).
  // Meta redirects to its own /meta/callback sub-route, which is handled by that route's component, not here.
  useEffect(() => {
    if (processedRef.current || isCallbackSubRoute) return;
    processedRef.current = true;

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const state = urlParams.get('state');
    const error = urlParams.get('error');

    if (error) {
      toast({
        title: 'Authorization Cancelled',
        description: urlParams.get('error_description') || 'HubSpot authorization was cancelled',
        variant: 'destructive',
      });
      navigate({ to: '/settings/account', hash: 'integrations', replace: true });
      return;
    }

    if (code && state) {
      createIntegrationMutation.mutate({ code, state });
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }

    // The standalone Integrations page is retired (#6372, #6670): a plain
    // visit lands on Settings -> Account -> Integrations, the one surface.
    // The route itself stays only for the provider callbacks above.
    navigate({ to: '/settings/account', hash: 'integrations', replace: true });
  }, [isCallbackSubRoute, createIntegrationMutation, toast, navigate]);

  if (isCallbackSubRoute) {
    return <div className="pt-1 space-y-6"><Outlet /></div>;
  }
  return completingHubspot ? <p className="pt-1 text-sm text-gray-700">Connecting HubSpot</p> : null;
}
